import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import { fromEmotionalStateData } from '../../domain/emotion/EmotionalStateContent';
import { calculateDailyProgress } from '../../domain/progression/CalculateDailyProgress';
import {
  transitionDailyStep,
  type DailyStepTransition,
} from '../../domain/routine/DailyStepTransitions';
import { selectActiveSteps } from '../../domain/routine/DayModeRules';
import type { DailyStepId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export class ChangeDailyStepStatus {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    stepId: DailyStepId,
    transition: DailyStepTransition,
  ): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      const steps = await this.dailySteps.listByRoutine(routine.id);
      const current = steps.find((step) => step.id === stepId);
      if (!current || current.profileId !== profileId) {
        return failure(new RoutineError('step_not_found', 'Mission introuvable.'));
      }

      const timestamp = this.clock.now().toISOString();
      const updatedStep = transitionDailyStep(current, transition, timestamp);
      const updatedSteps = steps.map((step) => (step.id === updatedStep.id ? updatedStep : step));
      const progress = calculateDailyProgress(selectActiveSteps(updatedSteps, routine.dayMode));
      const updatedRoutine = {
        ...routine,
        completionPercentage: progress.percentage,
        updatedAt: timestamp,
      };
      await this.transactions.updateRoutineAndStep(updatedRoutine, updatedStep);
      const log = await this.emotionalProtocolLogs.findByLogicalDate(profileId, logicalDate);
      return success({
        routine: updatedRoutine,
        steps: updatedSteps,
        progress,
        emotionalState: (log && fromEmotionalStateData(log.data)?.state) ?? null,
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
